// @givmo/design-system — PageHeader (SaaS layer).
//
// The header band at the top of every content screen: an optional breadcrumb, a page title
// (~22px / w700, text-strong), an optional subtitle (text-secondary), and a right-aligned
// actions slot (primary action / filters). Sits inside the content pane above the cards.
//
// ── The organization-name chip is the HEADER's, not a caller's (D-CP-56, CPA-WEB43) ─────────────
//
// A console page that belongs to one organization shows that organization's name at the right of
// this header. Seven pages used to compose their own `<Badge tone="neutral" size="md">{orgName}</Badge>`
// into `actions`, and a `Badge` is `whitespace-nowrap`: below `sm` this header stacks, the chip
// became a flex item whose automatic minimum size is the whole name, and a charity's 65-character
// legal name grew the DOCUMENT. Three live pages scrolled sideways at 375px in production
// (round full-round-20260904); they were cured one page at a time with `max-w-full` + a `truncate`
// label + `title`, and an independent review then found a FOURTH uncapped copy in `packages/crm`
// plus two more in compiled-dark pages. Jonathan's ruling (D-CP-56, 2026-09-04): the name WRAPS
// rather than truncating — `title` never appears on a phone, and on Profile that chip is an Owner's
// only sight of the name — done ONCE in the shared page header.
//
// So `organizationName` is a prop of this component and the chip is built here, wrap-capable.
// It is a LABEL, not a control: a building icon and the name in quiet text, with no border or fill,
// because a bordered pill beside a title reads as a button and a person clicks it to see what it does
// (Email Campaign polish, the KCC user-tester walk, 2026-10-31). Do NOT compose an organization-name badge into `actions`: this slot is opaque to
// the header, which cannot make an arbitrary child's label breakable without an `!important`
// descendant rule that would equally break every Button label and status chip the estate passes
// through it. `page-header-org-name-rail.test.ts` makes that repo-wide and executable.
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { Building2, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps extends TestIdProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional breadcrumb trail rendered above the title. */
  breadcrumbs?: Breadcrumb[];
  /** Right-aligned actions (buttons, menus). */
  actions?: ReactNode;
  /**
   * The organization this page belongs to. Rendered as the header's identity chip, ahead of
   * `actions`, wrapping onto as many lines as the lane needs so the whole legal name is readable at
   * phone width. Pass this — never a hand-rolled `<Badge>` in `actions` (see the header note above).
   * An empty string renders nothing, so a caller may pass an unresolved name straight through.
   */
  organizationName?: string;
  /** `data-testid` for the organization chip. Each page keeps its own (`<page>-org-badge`). */
  organizationNameTestId?: string;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  organizationName,
  organizationNameTestId,
  className,
  testId,
}: PageHeaderProps) {
  const orgChip =
    organizationName != null && organizationName !== '' ? (
      // `min-w-0` lets the label shrink below its own min-content in the header's flex row, and
      // `wrap-anywhere` breaks even a space-less name, so a long legal name never widens the page.
      <span
        className="flex min-w-0 items-start gap-6 font-sans text-s13 font-w500 tracking-givmo whitespace-normal text-text-secondary wrap-anywhere"
        {...testIdProp(organizationNameTestId)}
      >
        <Icon icon={Building2} size={14} className="mt-2 shrink-0 text-icon-muted" aria-hidden />
        <span className="min-w-0">{organizationName}</span>
      </span>
    ) : null;
  return (
    <div className={cn('flex flex-col gap-12', className)} {...testIdProp(testId)}>
      {breadcrumbs != null && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-4">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <Fragment key={`${crumb.label}-${i}`}>
                {crumb.href != null && !isLast ? (
                  <a
                    href={crumb.href}
                    className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary transition-colors hover:text-text-secondary"
                  >
                    {crumb.label}
                  </a>
                ) : (
                  <span
                    className={cn(
                      'font-sans text-s13 font-w500 tracking-givmo',
                      isLast ? 'text-text-secondary' : 'text-text-tertiary',
                    )}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
                {!isLast && (
                  <Icon icon={ChevronRight} size={14} className="text-icon-muted" aria-hidden />
                )}
              </Fragment>
            );
          })}
        </nav>
      )}
      {/* Stacked below `sm`: side-by-side, the shrink-0 actions leave the title + subtitle a
          sliver of a phone viewport (~110px next to two buttons), wrapping a word per line. */}
      <div className="flex flex-col gap-12 sm:flex-row sm:items-start sm:justify-between sm:gap-16">
        <div className="min-w-0">
          <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">{title}</h1>
          {subtitle != null && (
            <p className="mt-4 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              {subtitle}
            </p>
          )}
        </div>
        {/* The slot is unchanged. Containment lives on the label (it carries `min-w-0`),
            because a rule here would have to reach into an opaque child — and every Button label
            and status chip in the estate comes through this same slot. */}
        {(orgChip != null || actions != null) && (
          <div className="flex shrink-0 items-center gap-8">
            {orgChip}
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
